// @flow

import _ from 'lodash';
import { rem } from 'polished';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import { FORM_FIELD_NAME } from 'client/components/preRegistration/preRegistration';
import { isValidPasswordLength } from 'client/utils/validate';
import { PRE_REG_ACCOUNT_CREATE, PRE_REG_BACK } from 'client/constants/amplitudeTracker';
import { renderTextInput } from 'client/components/form/helpers';
import {
  RequiredFieldWrapper,
  ButtonWrapper,
} from 'client/components/preRegistration/registrationForm';
import COLOR from 'client/constants/colors';
import Loader from 'client/components/loader';
import RequiredField from 'client/components/form/requiredField';

import { Tracking } from 'web-core/src/tracking';

type Props = {
  backBtn: string,
  ctaBtn: string,
  header: string,
  isSubmitting: boolean,
  isValid: boolean,
  onCreate: Function,
  previousView: string,
  setView: Function,
  subheader: string,
  submitError: Object | null,
};

export const BackButtonWrapper = styled.button`
  width: 100%;
  color: ${COLOR.WHITE};
  &:hover {
    background-color: transparent;
    color: ${COLOR.WHITE};
  }
`;

const LoaderWrapper = styled.div`
  display: block;
  width: 100%;
  text-align: center;
`;

const ErrorMessageWrapper = styled.div`
  color: ${COLOR.NAVY_3};
  font-size: ${rem(14)};
  font-weight: bold;
`;

const passwordValidator = val =>
  (isValidPasswordLength(val) ? undefined : { path: 'common.error.passwordLength' });

const renderPasswordField = _.partial(renderTextInput, _, {
  label: {
    path: 'common.label.password',
  },
  placeholder: 'Password',
  noLabelAnimate: true,
  type: 'password',
});

const accountExistMessage = 'Looks like you already have a Stride account. Please ';
const defaultErrorMessage = 'Something went wrong. Please try your request again.';
const userExistError = 'user already exists';
const zipCodeError = 'Could not find address with zip code';

const renderErrorMessage = (error) => {
  // handle zip code enter error
  const hasZipCodeError = _.includes(_.get(error, 'message'), zipCodeError);
  if (hasZipCodeError) {
    Tracking.track(PRE_REG_ACCOUNT_CREATE, { status: zipCodeError });
    return <ErrorMessageWrapper>{error.message}</ErrorMessageWrapper>;
  }
  // handle existing user error
  const hasExistingAccountError = _.includes(_.get(error, 'response.data.message'), userExistError);
  if (hasExistingAccountError) {
    Tracking.track(PRE_REG_ACCOUNT_CREATE, { status: userExistError });
    return (
      <ErrorMessageWrapper>
        <span>
          {accountExistMessage}
          <a href="/login">login</a>
        </span>
      </ErrorMessageWrapper>
    );
  }
  Tracking.track(PRE_REG_ACCOUNT_CREATE, { status: 'Error' });
  return <ErrorMessageWrapper>{defaultErrorMessage}</ErrorMessageWrapper>;
};

const AccountCreate = (props: Props) => {
  const {
    backBtn,
    ctaBtn,
    header,
    isSubmitting,
    isValid,
    onCreate,
    previousView,
    setView,
    subheader,
    submitError,
  } = props;
  const [isDisabled, toggleDisabled] = useState(true);

  useEffect(() => {
    toggleDisabled(!isValid);
  });

  const handleClickBack = () => {
    Tracking.track(PRE_REG_BACK);
    setView(previousView);
  };

  const handleClickCreate = () => {
    onCreate();
  };

  return (
    <React.Fragment>
      <h1 className="small">{header}</h1>
      <h3>{subheader}</h3>
      <RequiredFieldWrapper>
        <RequiredField
          key={FORM_FIELD_NAME.PASSWORD}
          name={FORM_FIELD_NAME.PASSWORD}
          component={renderPasswordField}
          validate={[passwordValidator]} />
      </RequiredFieldWrapper>
      {isSubmitting && (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      )}
      {!isSubmitting && (
        <ButtonWrapper isDisabled={isDisabled} clickHandler={handleClickCreate}>
          {ctaBtn}
        </ButtonWrapper>
      )}
      {submitError && renderErrorMessage(submitError)}
      <BackButtonWrapper onClick={handleClickBack}>{backBtn}</BackButtonWrapper>
    </React.Fragment>
  );
};

export default AccountCreate;
